import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import vaultService from '../services/vaultService';
import hotelService from '../services/hotelService';
import accountService from '../services/accountService';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import Button from './shared/Button';
import './VaultSourceList.css';

const STATUS_FILTERS = [
  { key: 'all',            label: 'All',              statuses: null },
  { key: 'initiated',      label: 'Initiated',        statuses: ['initiated'] },
  { key: 'payment',        label: 'Payment',          statuses: ['payment'] },
  { key: 'download_app',   label: 'Download App',     statuses: ['client_generated', 'client_executed'] },
  { key: 'extraction',     label: 'Extraction',       statuses: ['connection_successful', 'records_extracted'] },
  { key: 'processing',     label: 'Processing',       statuses: ['records_processed'] },
  { key: 'tables_created', label: 'Tables Created',   statuses: ['vault_tables_created'] },
  { key: 'vault_ready',    label: 'Vault Ready',      statuses: ['vault_ready'] },
  { key: 'error',          label: 'Error',            statuses: ['error', 'inactive'] },
];

const VaultList = () => {
  const navigate = useNavigate();
  const { selectedAccount, selectedPartner, selectedHotel, canCreateProducts } = useAccount();
  const { isSuperAdmin } = useAuth();

  const [vaults, setVaults] = useState([]);
  const [hotels, setHotels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchVaults();
  }, [selectedAccount, selectedPartner, selectedHotel, showDeleted]); // eslint-disable-line

  const fetchVaults = async () => {
    try {
      setLoading(true);
      setError(null);

      let vaultsList = [];
      const filters = isSuperAdmin && showDeleted ? { showDeleted: true } : {};

      if (selectedHotel) {
        const response = await vaultService.getAllVaults({ ...filters, hotelid: selectedHotel });
        vaultsList = response.vaults || [];
      } else if (selectedAccount) {
        const hotelsResponse = await hotelService.getAllHotels({ accountid: selectedAccount });
        const accountHotelIds = new Set((hotelsResponse.hotels || []).map(h => h.hotelid));
        const response = await vaultService.getAllVaults(filters);
        vaultsList = (response.vaults || []).filter(v => accountHotelIds.has(v.hotelid));
      } else if (selectedPartner) {
        const accountsResponse = await accountService.getAllAccounts({ partnerid: selectedPartner });
        const partnerAccounts = accountsResponse.accounts || [];
        const hotelResponses = await Promise.all(
          partnerAccounts.map(a => {
            const accountId = a.ACCOUNTID ?? a.id;
            if (!accountId) return Promise.resolve({ hotels: [] });
            return hotelService.getAllHotels({ accountid: accountId }).catch(() => ({ hotels: [] }));
          })
        );
        const partnerHotelIds = new Set(
          hotelResponses.flatMap(r => (r.hotels || []).map(h => h.hotelid))
        );
        const response = await vaultService.getAllVaults(filters);
        vaultsList = (response.vaults || []).filter(v => partnerHotelIds.has(v.hotelid));
      } else {
        const response = await vaultService.getAllVaults(filters);
        vaultsList = response.vaults || [];
      }

      setVaults(vaultsList);

      const hotelIds = [...new Set(vaultsList.map(v => v.hotelid).filter(Boolean))];
      const hotelData = {};
      await Promise.all(
        hotelIds.map(async (hotelId) => {
          try {
            const hotel = await hotelService.getHotelById(hotelId);
            hotelData[hotelId] = hotel.hotelname || hotel.HOTELNAME || `Hotel ${hotelId}`;
          } catch {
            hotelData[hotelId] = `Hotel ${hotelId}`;
          }
        })
      );
      setHotels(hotelData);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load vaults');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, vault) => {
    e.stopPropagation();
    if (!window.confirm(`Delete vault "${vault.name || vault.id}"? This cannot be undone. Any active Stripe subscription will be cancelled.`)) return;
    setDeletingId(vault.id);
    try {
      await vaultService.deleteVault(vault.id);
      setVaults(prev => prev.filter(v => v.id !== vault.id));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete vault');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':     return 'status-badge status-active';
      case 'initiated':     return 'status-badge status-pending';
      case 'client_executed': return 'status-badge status-pending';
      case 'error':         return 'status-badge status-inactive';
      default:              return 'status-badge';
    }
  };

  const filteredVaults = vaults.filter(vault => {
    const name = (vault.name || '').toLowerCase();
    const hotelName = (hotels[vault.hotelid] || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    const matchesSearch = name.includes(term) || hotelName.includes(term);
    const activeFilter = STATUS_FILTERS.find(f => f.key === statusFilter);
    const matchesStatus = !activeFilter?.statuses || activeFilter.statuses.includes(vault.status);
    return matchesSearch && matchesStatus;
  });

  if (loading) return <div className="loading">Loading vaults...</div>;

  return (
    <div className="vaultsource-list-container">
      <div className="header">
        <h1>Vaults</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {isSuperAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6b7280', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={e => setShowDeleted(e.target.checked)}
              />
              Show deleted
            </label>
          )}
          {/* Buying a vault is an admin act ('A' on the property, its chain or its partner) — the
              lambda has enforced it since the feature shipped; this stops the wizard being offered to
              someone whose save can only 403. */}
          {canCreateProducts && (
            <Button variant="success" onClick={() => navigate('/vaults/new')}>
              <Plus className="icon" />
              Add Vault
            </Button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name or hotel..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="status-toggle">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              className={`status-toggle-btn${statusFilter === f.key ? ' active' : ''}`}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="table-container">
        <table className="vaultsource-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Hotel</th>
              <th>Vault Source</th>
              <th>Status</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVaults.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {vaults.length === 0 ? 'No vaults found' : 'No vaults match your search'}
                </td>
              </tr>
            ) : (
              filteredVaults.map((vault) => (
                <tr
                  key={vault.id}
                  onClick={() => !vault.deleted_at && navigate(`/vault/${vault.id}`)}
                  className={vault.deleted_at ? '' : 'clickable-row'}
                  title={vault.deleted_at ? `Deleted ${new Date(vault.deleted_at).toLocaleDateString('en-GB')}` : 'Click to view'}
                  style={vault.deleted_at ? { opacity: 0.5, background: '#fafafa' } : {}}
                >
                  <td>
                    {vault.name || '—'}
                    {vault.deleted_at && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#ef4444', fontWeight: 600 }}>DELETED</span>
                    )}
                  </td>
                  <td>{hotels[vault.hotelid] || vault.hotelid || '—'}</td>
                  <td>{vault.vault_source || '—'}</td>
                  <td>
                    <span className={getStatusBadgeClass(vault.status)}>
                      {vault.status || '—'}
                    </span>
                  </td>
                  <td>{vault.active === 'Y' ? '✓' : '✗'}</td>
                  <td className="actions">
                    {!vault.deleted_at && (
                      <button
                        className="btn btn-edit"
                        onClick={(e) => { e.stopPropagation(); navigate(`/vault/${vault.id}/edit`); }}
                        title="Edit"
                      >
                        ✏️
                      </button>
                    )}
                    {isSuperAdmin && !vault.deleted_at && (
                      <button
                        className="btn btn-delete"
                        onClick={(e) => handleDelete(e, vault)}
                        disabled={deletingId === vault.id}
                        title="Delete vault"
                        style={{ marginLeft: '4px' }}
                      >
                        {deletingId === vault.id ? '...' : '🗑️'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VaultList;
